<template>
  <v-container class="px-0">
    <v-row v-if="displayMode === 'masonry'">
      <v-col cols="4" v-for="(column, index) in masonryState.columns" :key="index">
        <feed-card 
          v-for="item in column.items"
          :key="`news_radar_data_${item.id}`"
          :item="item"
          @height-calculated="(height) => updateMasonryColumnHeight(index, height)"
        />
      </v-col>
    </v-row>

    <feed-card 
      v-else
      v-for="item in pagination.items" 
      :item="item" 
      :key="`news_radar_data_${item.id}`" 
    />

    <div ref="infiniteScrollSentinel" style="height: 1px;"></div>

    <back-to-top />

    <v-progress-circular
      v-if="page.loading"
      indeterminate
      color="primary"
      class="my-4"
    />
  </v-container>
</template>

<style scoped>
</style>

<script>
import { computed, reactive, watch } from "@vue/composition-api";

import BackToTop from '@/components/common/buttons/BackToTop.vue';
import FeedCard from '@/components/news-radar/db/cards/FeedCard.vue';

import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import useMasonry from "@/hooks/useMasonry";
import useProxy from "@/hooks/useProxy";
import useNewsRadarDataQuery from "@/components/news-radar/db/useNewsRadarDataQuery";

export default {
  name: "FeedResults",
  components: {
    BackToTop,
    FeedCard,
  },
  setup(props, { emit }) {
    const { store } = useProxy();
    const { applyFilters, fetchPage, hasValidDates } = useNewsRadarDataQuery();
    const { manageInfiniteScroll, sentinel } = useInfiniteScroll(async () => {
      pagination.options.page += 1;
      page.forceCall = true;

      await getData();
    });
    const {loadMasonryItems, masonryState, resetMasonry, updateMasonryColumnHeight} = useMasonry(3);
  
    const page = reactive({
      loading: false,
      forceCall: false,
    });

    const pagination = reactive({
      total: 0,
      items: [],
      options: {
        page: 1,
        itemsPerPage: 25,
      },
    });

    // =>  METHODS
    const checkDates = () => {
      if (!hasValidDates()) {
        alert("Sélectionnez une plage de date !");
        return false;
      }

      return true;
    };

    const formatData = (items) =>
      items.map((item) => {
        const matches = item.publication_date.match(
          /((\d{4})-(\d{2})-(\d{2}))T(\d{2}:\d{2}:\d{2})/
        );

        return {
          ...item,
          source: item.news_radar_source.name,
          publication_date: {
            date: matches[1],
            yyyy: matches[2],
            mm: matches[3],
            dd: matches[4],
            time: matches[5],
            full: item.publication_date,
          },
        };
      });
    
    const gotData = () => {
      if (applyFilters.value) {
        // Force clicking on button again to re-run that request :
        // (we don't call this dispatch earlier to be sure, as state is async, that all listening components could run their requests)
        store.dispatch("common/updateApplyFilters", false);
      }

      page.forceCall = false;
    };

    const getData = async () => {
      if ((applyFilters.value || page.forceCall) && !page.loading) {
        if (!checkDates()) {
          gotData();

          return;
        }

        try {
          page.loading = true;

          const { total, items } = await fetchPage({
            page: pagination.options.page,
            limit: pagination.options.itemsPerPage,
          });

          pagination.total = total;
          const formattedItems = formatData(items);

          if (pagination.options.page == 1) {
            pagination.items = formattedItems;
          } else {
            pagination.items.push(...formattedItems);
          }

          if (displayMode.value === 'masonry') {
            loadMasonryItems(formattedItems);
          }

          manageInfiniteScroll(pagination.items.length, pagination.total);
        } catch (error) {
          console.error("error while fetching data");
        }

        page.loading = false;
      }

      gotData();
    };

    // =>  COMPUTED
    const displayMode = computed(() => store.getters['newsRadar/getDisplayMode']);

    // =>  WATCH
    watch(applyFilters, async (newValue) => {
      if (newValue) {
        // new search:
        pagination.total = 0;
        pagination.items = [];
        pagination.options.page = 1;
        resetMasonry();

        await getData();
      }
    });

    watch(displayMode, () => {
      pagination.total = 0;
      pagination.items = [];
      pagination.options.page = 1;

      resetMasonry();
    });

    // =>  HOOKS

    // =>  DATA
    return {
      displayMode,
      masonryState,
      page,
      pagination,
      infiniteScrollSentinel: sentinel,
      updateMasonryColumnHeight,
    };
  },
};
</script>
